// ⚠ THIS FILE EXISTS TWICE, BYTE FOR BYTE:
//     redis_dev/helpers/mergeSlot.js          — the launch builds the keys
//     eng-fontend-v1/src/helpers/mergeSlot.js — the wizard previews them
//
// The two services are separate packages with their own node_modules and
// neither can import from the other, which is why this is a copy rather than a
// dependency. `tests/mergeSlot.test.js` compares the two files and fails the
// moment they drift: a pairing the wizard previews and the launch does not
// produce is worse than no preview at all.
//
// Same arrangement, and the same reason, as `coilToken.js`. Deliberately
// dependency-free so the copies can stay identical.
//
// Which executions share one drum.
//
// ── the problem this exists to fix ──────────────────────────────────────────
//
// A merged phase pairs executions by POSITION: `mergeQueueKey` is
// `<baseKey>::slot_N` where N is a per-job running counter over the executions
// sharing a base key (colour at conductor_isolation, wire-Ø at unwinding). Two
// executions land on one drum iff base key AND slot match. So the Nth
// green-yellow of product A always pairs with the Nth green-yellow of product B.
//
// That breaks the moment the products need a different number of drums:
//
//   MYYM 5x4, 20 000 m green-yellow -> two drums, 13 000 + 7 000   (slots 0, 1)
//   MYYM 3x4,  5 000 m green-yellow -> one drum,   5 000           (slot 0)
//
// Positionally the 5 000 joins the 13 000 and you get an 18 000 m drum. What
// the planner wanted was the 5 000 on the 7 000 — a 12 000 m drum — with the
// 13 000 left alone. There was no way to say so.
//
// Same class of bug as the tandem pairing fix: pairing by position instead of
// by identity. The cure is the same — let the planner name the partner.
//
// ── why this is a small change ──────────────────────────────────────────────
//
// The slot number IS already the pairing identity; it was simply never
// choosable. Nothing downstream parses it — the queue, the HMI's
// sameSharedExecution(), Factory 3D's sharedTokens, reconcile and the drum
// ledger all treat `mergeQueueKey` as an opaque equality key, and the actual
// sharing travels on `tokenId`. So making N explicit changes who shares a drum
// and nothing else.
//
// N-way falls out for free: resolveMergedTokenId keeps a registry keyed by
// mergeQueueKey, so three, four or ten executions carrying the same slot all
// resolve to one token.
//
// ── two passes, and why ─────────────────────────────────────────────────────
//
// Explicit slots are reserved BEFORE auto slots are handed out. One pass would
// let a running counter take slot 1 before the execution that explicitly asked
// for slot 1 got there, silently merging the wrong pair — the exact failure
// this is meant to end.

const normalizeMergeValue = (v) => String(v ?? "").trim().toUpperCase();

/**
 * WHICH executions are even candidates to share a drum — the "base key".
 *
 * Two executions can merge only when this matches AND their slot matches
 * (`planMergeSlots` below). What makes them comparable differs per phase, and it
 * is the physical question each time:
 *
 *   · `unwinding`            the wire diameter being paid off
 *   · `conductor_isolation`  the colour going through the extruder
 *   · `rope`                 the machine and the bobbin it winds onto
 *   · everything else        machine, bobbin, where it is going, and colour
 *
 * Lived TWICE in the backend until 2026-10-19 — `helpers/jobHelpers.js` for
 * create-new and a hand-copied replica in `controller/jobs.js` for
 * plan-next-phase, whose comment said it "replicates" the other so the two
 * flows would agree. Nothing enforced that. One definition now, here beside the
 * slot plan it is always used with.
 *
 * Takes `phase` (an object) or `phaseName` (a string), because the two callers
 * had different shapes and neither should have to change to use this.
 */
export function buildMergedOutputKey({ phase, phaseName, exec } = {}) {
  const name = String(phaseName ?? phase?.name ?? "");

  if (name === "unwinding") {
    return ["MERGE", name, normalizeMergeValue(exec?.wireDiameter)].join("::");
  }

  if (name === "conductor_isolation") {
    return ["MERGE", name, normalizeMergeValue(exec?.color)].join("::");
  }

  if (name === "rope") {
    return [
      "MERGE",
      name,
      normalizeMergeValue(exec?.machineId),
      normalizeMergeValue(exec?.drum_material),
      normalizeMergeValue(exec?.drum_diameter),
    ].join("::");
  }

  return [
    "MERGE",
    name,
    normalizeMergeValue(exec?.machineId),
    normalizeMergeValue(exec?.drum_material),
    normalizeMergeValue(exec?.drum_diameter),
    normalizeMergeValue(exec?.deliveryMode),
    normalizeMergeValue(exec?.color),
  ].join("::");
}

/** Is this phase one the launch was told to merge across products? */
export function isPhaseMerged(mergedPhases, phaseName) {
  return Boolean(mergedPhases && mergedPhases[phaseName] === true);
}

/**
 * A slot the planner chose, or null when they left it to us.
 *
 * Only numbers and numeric strings count. `Number([])` is 0 and `Number(true)`
 * is 1 — without the typeof guard an empty array arriving over JSON would
 * become slot 0 and silently merge two products that never asked to be.
 */
export function normalizeMergeSlot(v) {
  if (typeof v !== "number" && typeof v !== "string") return null;
  if (v === "") return null;
  const n = Number(v);
  if (!Number.isInteger(n) || n < 0) return null;
  return n;
}

/**
 * The bucket an execution lands in — the key two executions must SHARE to
 * become one bobbin.
 *
 * ── AN EXPLICIT GROUP IS THE PLANNER NAMING THE PARTNER ──────────────────
 *
 * On AUTOMATIC the base key stands, and it has to: slots are handed out 0,1,2…
 * positionally, and without the colour (or the reel, or the wire Ø) one
 * product's blue would pair with another's brown purely by counting. The base
 * key is what makes automatic numbering safe.
 *
 * On an EXPLICIT group it is the opposite. The planner has pointed at two rows
 * and said "these, one drum" — there is nothing left for the key to protect
 * against, and keeping the base key means the instruction cannot be carried out
 * at all. Reported 2026-11-02: three MYF 1x2.5 launched together, green-yellow /
 * red / blue, GROUP 1 typed on a 20 000 m row of two of them:
 *
 *   *"Nimic nu pare sa functioneze orice fac nu mi-l lanseaza in merge
 *   execution."*
 *
 * The slots were honoured — both came out 0 — but the keys were
 * `…::GREEN-YELLOW::slot_0` and `…::RED::slot_0`, so they could never meet:
 *
 *     MERGE::conductor_isolation::GREEN-YELLOW::slot_0
 *     MERGE::conductor_isolation::RED::slot_0          → different buckets
 *
 * A reel holding 20 000 m of green-yellow and then 20 000 m of red is an
 * ordinary thing to want on a Ø1250 wooden drum; the colour is the planner's
 * call, not the key's.
 *
 * THE MACHINE IS NOT. One bobbin is wound on one line, so an explicit key keeps
 * it — that is a physical fact and no instruction can override it. (A rope's
 * base key already carried it; isolation's never did, which is a separate hole
 * this does not widen.)
 *
 * @param explicit true when the planner chose the group, false when it was
 *                 numbered automatically
 */
export function buildMergeQueueKey({ phaseName, phase, exec, slot, explicit = false } = {}) {
  const name = String(phaseName ?? phase?.name ?? "");
  if (explicit) {
    return ["MERGE", name, normalizeMergeValue(exec?.machineId), `group_${slot}`].join("::");
  }
  return `${buildMergedOutputKey({ phaseName: name, exec })}::slot_${slot}`;
}

export class MergeSlotConflict extends Error {
  constructor(jobKey, baseKey, slot) {
    super(
      `Two executions of the same product were both put on merge slot ${slot} ` +
        `(${baseKey}). A product cannot be merged with itself — give one of them ` +
        `a different group, or leave it unmerged.`
    );
    this.name = "MergeSlotConflict";
    this.jobKey = jobKey;
    this.baseKey = baseKey;
    this.slot = slot;
  }
}

/**
 * Slots at or above this are handed out one per execution and are never offered
 * in the wizard, so nothing can land on one by choosing a group. That is what
 * makes "do not merge" a guarantee rather than a hope: leaving an execution on
 * automatic only keeps it alone while no OTHER product happens to be numbered
 * into the same slot, which is luck, not an instruction.
 */
export const SOLO_SLOT_BASE = 1000;

/**
 * Decide the merge slot for every merged execution in one submission.
 *
 * @param items [{ id, jobKey, baseKey, mergeSlot }]
 *   `id`       whatever the caller wants back as the lookup key
 *   `jobKey`   the product/job this execution belongs to. Slots are unique
 *              WITHIN a job and shared ACROSS jobs — that is what merging is.
 *   `baseKey`  buildMergedOutputKey's output (colour, wire-Ø, …)
 *   `mergeSlot` the planner's choice, or null/undefined for automatic
 *   `mergeSolo` true = keep this execution on a drum of its own, guaranteed
 * @returns Map<id, slot>
 * @throws  MergeSlotConflict if one job puts two executions on one slot
 */
export function planMergeSlots(items = []) {
  const out = new Map();
  // jobKey -> baseKey -> Set(slot)
  const taken = new Map();
  const takenFor = (jobKey, baseKey) => {
    let byBase = taken.get(jobKey);
    if (!byBase) { byBase = new Map(); taken.set(jobKey, byBase); }
    let set = byBase.get(baseKey);
    if (!set) { set = new Set(); byBase.set(baseKey, set); }
    return set;
  };

  // pass 0 — "do not merge this one". Counted per base key ACROSS jobs, not
  // within one: two products both given solo must get DIFFERENT slots, or they
  // would meet each other and merge — the very thing they asked not to do.
  const soloNext = new Map();
  for (const it of items) {
    if (!it.mergeSolo) continue;
    const n = soloNext.get(it.baseKey) ?? 0;
    soloNext.set(it.baseKey, n + 1);
    const slot = SOLO_SLOT_BASE + n;
    takenFor(it.jobKey, it.baseKey).add(slot);
    out.set(it.id, slot);
  }

  // pass 1 — honour what the planner asked for
  for (const it of items) {
    if (out.has(it.id)) continue;
    const slot = normalizeMergeSlot(it.mergeSlot);
    if (slot === null) continue;
    const set = takenFor(it.jobKey, it.baseKey);
    if (set.has(slot)) throw new MergeSlotConflict(it.jobKey, it.baseKey, slot);
    set.add(slot);
    out.set(it.id, slot);
  }

  // pass 2 — fill the rest into the lowest free slot, which reproduces the old
  // 0,1,2… numbering exactly when nothing is explicit
  for (const it of items) {
    if (out.has(it.id)) continue;
    const set = takenFor(it.jobKey, it.baseKey);
    let slot = 0;
    while (set.has(slot)) slot += 1;
    set.add(slot);
    out.set(it.id, slot);
  }

  return out;
}

/**
 * Who actually ends up sharing a drum — for validating a plan before it is
 * written, and for telling the planner what they just asked for.
 *
 * @returns [{ baseKey, slot, members: [id], jobKeys: [jobKey] }] for slots held
 *          by MORE THAN ONE job. A slot with a single occupant is not a merge,
 *          however it was numbered.
 */
export function describeMerges(items = [], slots = new Map()) {
  const groups = new Map();
  for (const it of items) {
    const slot = slots.get(it.id);
    if (slot === undefined) continue;
    const k = `${it.baseKey}::slot_${slot}`;
    let g = groups.get(k);
    if (!g) { g = { baseKey: it.baseKey, slot, members: [], jobKeys: new Set() }; groups.set(k, g); }
    g.members.push(it.id);
    g.jobKeys.add(it.jobKey);
  }
  return [...groups.values()]
    .filter(g => g.jobKeys.size > 1)
    .map(g => ({ ...g, jobKeys: [...g.jobKeys] }));
}
